import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import Lightbulb from 'Icons/Lightbulb';
import Fac from 'Icons/Fac';
import Home from 'Icons/Home';
import Chart from 'Icons/Chart';
import Notepad from 'Icons/Notepad';
import MagicWand from 'Icons/MagicWand';
import Tasks from 'Icons/Tasks';
import PiggyBank from 'Icons/PiggyBank';
import useQueryParams from 'Hooks/useQueryParams';
import CreditCardIcon from '@material-ui/icons/CreditCard';

import {
  FACET_PLAN_OUTCOME_PRIORITIZATION_ITEM,
  FACET_PLAN_SAC,
  FACET_PLAN_SIDEBAR_QUERY_PARAM,
  FACET_PLAN_VIEW_SIDEBAR_ASSUMPTIONS,
  FACET_PLAN_VIEW_SIDEBAR_EXPENSES,
  FACET_PLAN_VIEW_SIDEBAR_LOGS,
  FACET_PLAN_VIEW_SIDEBAR_PROPOSALS,
  FACET_PLAN_VIEW_SIDEBAR_SCENARIOS,
  FACET_PLAN_VIEW_SIDEBAR_ARCHIVES,
  FACET_PLAN_VIEW_SIDEBAR_SUMMARY,
  FACET_PLAN_VIEW_SIDEBAR_BALANCE_SHEET,
  FACET_PLAN_VIEW_SIDEBAR_RESOURCES,
  FACET_PLAN_VIEW_SIDEBAR_DESIRED_OUTCOMES,
  FACET_PLAN_VIEW_SIDEBAR_JOBS,
  FACET_PLAN_VIEW_SIDEBAR_AUTOMATION_SUGGESTIONS,
  FACET_PLAN_CURRENT_MONTH_CONFIGURATIONS,
  TEMPLATE_TYPE_AUTOMATED,
  TEMPLATE_TYPE_MESSAGE_MEETING_COMBO,
  TEMPLATE_TYPE_NOTE, FACET_PLAN_SAC_OUTCOMES,
} from 'Views/Page/Content/Templates/WhatIfTemplate/Components/Sidebar/consts';
import FacetTabs from 'Components/ComponentLibrary/Molecules/Tabs';
import classNames from 'classnames';
import { Schedule } from '@material-ui/icons';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import BarLineChart from 'Icons/BarLineChart';
import throwInDevOutsideReact from 'Helpers/throwInDevOutsideReact';
import fr from 'facet-rest';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

const styles = (theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: 84,
    backgroundColor: theme.palette.facet.lightGray5,
    alignItems: 'center',
    '&:has(> * > .MuiTabs-root > .MuiTabs-scrollable:first-child)': {
      paddingTop: theme.spacing(2),
    },
  },
  label: {
    fontSize: 10,
    fontWeight: theme.typography.fontWeightBold,
    padding: theme.spacing(0.5, 0),
  },
  indicator: {
    width: 6,
    borderRadius: theme.radius.md,
    background: theme.gradients.vertical.primaryToAccentBlue,
  },
  selectedNav: {
    backgroundColor: theme.palette.common.white,
  },
  unselectedNav: {
    backgroundColor: theme.palette.facet.backgroundGray,
  },
  iconWrapper: {
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[300],
    borderRadius: theme.radius.xl,
    '& svg': {
      fill: theme.palette.grey[600],
    },
  },
  selected: {
    backgroundColor: theme.palette.common.white,
    '& svg': {
      fill: theme.palette.primary.main,
    },
  },
  tabs: {
    maxWidth: '100%',
    flex: 1,
    display: 'flex',
    minHeight: 0,
  },
  tab: {
    minWidth: 'unset',
  },
  backContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    flexDirection: 'column',
    marginRight: theme.spacing(1),
  },
});

const inaccessibleTabs = ['FacetPlanViewSidebarBalanceSheet', 'FacetPlanViewSidebarIncome'];

const getTabIcon = (resource) => {
  switch (resource.attr('key')) {
    case FACET_PLAN_VIEW_SIDEBAR_SUMMARY: {
      return Home;
    }
    case FACET_PLAN_OUTCOME_PRIORITIZATION_ITEM: {
      return Chart;
    }
    case FACET_PLAN_SAC: {
      return Chart;
    }
    case FACET_PLAN_SAC_OUTCOMES: {
      return Chart;
    }
    case FACET_PLAN_VIEW_SIDEBAR_ASSUMPTIONS: {
      return Lightbulb;
    }
    case FACET_PLAN_VIEW_SIDEBAR_SCENARIOS: {
      return Chart;
    }
    case FACET_PLAN_VIEW_SIDEBAR_PROPOSALS: {
      return Lightbulb;
    }
    case FACET_PLAN_VIEW_SIDEBAR_ARCHIVES: {
      return Fac;
    }
    case FACET_PLAN_VIEW_SIDEBAR_EXPENSES: {
      return CreditCardIcon;
    }
    case FACET_PLAN_VIEW_SIDEBAR_BALANCE_SHEET: {
      return BarLineChart;
    }
    case FACET_PLAN_VIEW_SIDEBAR_LOGS: {
      return Schedule;
    }
    case TEMPLATE_TYPE_NOTE: {
      return Notepad;
    }
    case TEMPLATE_TYPE_AUTOMATED: {
      return MagicWand;
    }
    case TEMPLATE_TYPE_MESSAGE_MEETING_COMBO: {
      return Tasks;
    }
    case FACET_PLAN_VIEW_SIDEBAR_RESOURCES: {
      return PiggyBank;
    }
    case FACET_PLAN_VIEW_SIDEBAR_DESIRED_OUTCOMES: {
      return Chart;
    }
    case FACET_PLAN_VIEW_SIDEBAR_AUTOMATION_SUGGESTIONS: {
      return MagicWand;
    }
    case FACET_PLAN_CURRENT_MONTH_CONFIGURATIONS: {
      return Chart;
    }
    case FACET_PLAN_VIEW_SIDEBAR_JOBS: {
      return PiggyBank;
    }
    default: {
      return Home;
    }
  }
};

const BACK_TAB_ID = 'back';

const Sidebar = ({
  classes,
  onChange,
  className,
}) => {
  const { logStandardizedEvent } = useEventTrackingLogEvent();
  const [params, setParams] = useQueryParams();
  const { pageContentResource, loadPageContentLink } = usePageContentContext();
  const filteredTabs = pageContentResource.attr('sidebarTabs')
    .resources()
    .list()
    .filter(t => !inaccessibleTabs.includes(t.attr('key')));

  // Blow up in dev if assumption is broken
  if (filteredTabs.length === 0) {
    throwInDevOutsideReact('Unexpected missing tabs in dev');
  }

  const tabParam = params.get(FACET_PLAN_SIDEBAR_QUERY_PARAM) || filteredTabs[0]?.attr('key') || null;
  const [currentTab, setCurrentTab] = React.useState(tabParam);

  function onTabChange(value) {
    setCurrentTab(value);
    logStandardizedEvent(categories.whatIf, eventTypes.navigateTo, `changing tab to ${value}`, {
      eventProperties: {
        navigateTo: value,
        scenarioId: pageContentResource.id(),
      },
    });
  }

  React.useEffect(() => {
    // If the current tab is not in the list of tabs, set the current tab to the first tab
    if (filteredTabs.filter(t => t.attr('key') === currentTab).length === 0) {
      setCurrentTab(filteredTabs[0]?.attr('key') || null);
    }

    if (currentTab) {
      params.set(FACET_PLAN_SIDEBAR_QUERY_PARAM, currentTab);
      setParams(params);
    }
  }, [currentTab]);

  React.useEffect(() => {
    if (tabParam && tabParam !== currentTab) {
      setCurrentTab(tabParam);
    }
  }, [tabParam]);

  const tabsToRender = filteredTabs.map(t => {
    const Icon = getTabIcon(t);
    const isSelected = t.attr('key') === currentTab;

    return ({
      key: t.attr('key'),
      label: t.attr('label'),
      icon: (
        <span className={classNames(classes.iconWrapper, {
          [classes.selected]: isSelected,
        })}
        >
          <Icon />
        </span>),
      value: t.attr('key'),
      isHidden: t.attr('hidden') === 'true',
    });
  });
  const { backButton, goBack } = useMemo(() => pageContentResource.withRelationship(
    fr.relationships.scenariosPage.name,
    () => ({
      backButton: {
        key: BACK_TAB_ID,
        label: 'Back',
        icon: <span className={classes.iconWrapper}><ArrowBackIcon /></span>,
        value: BACK_TAB_ID,
      },
      goBack: () => loadPageContentLink(pageContentResource.relationship(fr.relationships.scenariosPage.name).link('related')),
    }),
    () => ({
      goBack() {},
    }),
  ), [pageContentResource, loadPageContentLink]);
  if (backButton) {
    tabsToRender.unshift(backButton);
  }


  return (
    <Box className={classNames(classes.root, className)}>
      <FacetTabs
        variant="scrollable"
        className={classes.tabs}
        classes={{
          label: classes.label,
          indicator: classes.indicator,
        }}
        TabProps={{
          classes: {
            root: classes.tab,
          },
        }}
        onChange={(event, value) => { // eslint-disable-line
          if (value === BACK_TAB_ID) {
            goBack();
            return;
          }
          onTabChange(value);
          onChange(value);
        }}
        value={currentTab}
        orientation="vertical"
        tabs={tabsToRender}
      />
    </Box>
  );
};

Sidebar.defaultProps = {
  onChange: () => {},
  className: '',
};

Sidebar.propTypes = {
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
  onChange: PropTypes.func,
};

export default withStyles(styles)(Sidebar);
